import type { Traduttore } from "../../i18n/index.ts";
import { useT } from "../lib/i18n.tsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { defaultKeymap, history, historyKeymap, indentLess, indentMore } from "@codemirror/commands";
import {
  codeFolding,
  foldGutter,
  foldKeymap,
  foldService,
  indentService,
  indentUnit,
} from "@codemirror/language";
import {
  findNext,
  openSearchPanel,
  search,
  searchKeymap,
  SearchQuery,
  setSearchQuery,
} from "@codemirror/search";
import { EditorState, RangeSetBuilder, Transaction, type Extension } from "@codemirror/state";
import {
  Decoration,
  drawSelection,
  EditorView,
  keymap,
  lineNumbers,
  placeholder as segnaposto,
  ViewPlugin,
  type DecorationSet,
  type ViewUpdate,
} from "@codemirror/view";
import { highlightEnvLine, highlightYamlLine, type Token } from "../lib/highlight.ts";
import { Button, cx } from "./ui.tsx";

/**
 * Editor di testo con evidenziazione, ricerca e ripiegamento dei blocchi.
 *
 * Il motore è CodeMirror: un'area di testo non sa nascondere righe, e il
 * ripiegamento è proprio questo. Il colore resta quello del tokenizzatore di
 * `highlight.ts`, applicato riga per riga a quelle visibili, così compose e
 * environment si leggono come prima — variabili comprese.
 */

const COLORI: Record<Token["type"], string> = {
  comment: "text-neutral-500 italic",
  key: "text-sky-300",
  string: "text-emerald-300",
  number: "text-orange-300",
  boolean: "text-purple-300",
  variable: "text-amber-300 font-medium",
  punct: "text-neutral-400",
  text: "text-neutral-100",
};

export type Linguaggio = "yaml" | "env";

const EVIDENZIATORI: Record<Linguaggio, (riga: string) => Token[]> = {
  yaml: highlightYamlLine,
  env: highlightEnvLine,
};

/** Larghezza di un livello di indentazione, in spazi. */
const INDENT = "  ";

export interface CodeEditorProps {
  value: string;
  onChange?: (value: string) => void;
  linguaggio: Linguaggio;
  readOnly?: boolean;
  /** Righe visibili prima che compaia lo scorrimento; ignorato se c'è `altezza`. */
  righe?: number;
  /** Altezza esatta in pixel, per adattarsi allo spazio disponibile. */
  altezza?: number;
  placeholder?: string;
  /** Numeri di riga e ripiegamento: utili sul compose, superflui su pochi valori. */
  numeriDiRiga?: boolean;
  /** Mostra il comando per lavorare a schermo intero. */
  espandibile?: boolean;
  /** Abilita la ricerca nel testo, con Ctrl/Cmd+F e Invio per scorrere. */
  ricercabile?: boolean;
  /**
   * Termine su cui aprire la ricerca appena l'editor compare.
   *
   * Serve a chi arriva da altrove sapendo già cosa cercare — dalla verifica, per
   * esempio — così non deve ripetere a mano un'informazione che si ha già.
   */
  ricercaIniziale?: string;
  /** Nome mostrato nella barra a schermo intero. */
  titolo?: string;
  /**
   * Comandi disponibili anche a schermo intero.
   *
   * Senza di essi si dovrebbe uscire per salvare, e un editor che costringe a
   * chiudersi per confermare il lavoro è peggio che non averlo espanso.
   */
  azioni?: React.ReactNode;
}

/** Interlinea del testo, in multipli della dimensione del carattere. */
const INTERLINEA = 1.6;

const rientro = (testo: string) => /^[ \t]*/.exec(testo)![0].length;

/**
 * Un blocco si ripiega fino all'ultima riga più rientrata di lui: in YAML la
 * struttura è il rientro, e basta questo per piegare servizi, ambienti, liste.
 */
// ponytail: scansione lineare per ogni riga del margine, O(n²) sul documento; basta per un compose
const ripiegaPerRientro = foldService.of((state, inizio) => {
  const riga = state.doc.lineAt(inizio);
  if (!riga.text.trim()) return null;

  const base = rientro(riga.text);
  let fine = riga.to;

  for (let numero = riga.number + 1; numero <= state.doc.lines; numero++) {
    const successiva = state.doc.line(numero);
    if (!successiva.text.trim()) continue;
    if (rientro(successiva.text) <= base) break;
    fine = successiva.to;
  }

  return fine > riga.to ? { from: riga.to, to: fine } : null;
});

/**
 * Invio mantiene il rientro, e dopo una chiave senza valore lo aumenta: in YAML
 * il contenuto di una chiave aperta va un livello più a destra.
 */
const rientroYaml = indentService.of((contesto, posizione) => {
  const riga = contesto.lineAt(posizione, -1);
  const prima = riga.text.slice(0, posizione - riga.from);

  return rientro(prima) + (/:\s*$/.test(prima) ? INDENT.length : 0);
});

/** Tab inserisce il rientro al cursore, o sposta l'intera selezione. */
const tasti = keymap.of([
  {
    key: "Tab",
    run: (vista) => {
      if (vista.state.readOnly) return false;
      if (!vista.state.selection.main.empty) return indentMore(vista);

      vista.dispatch(vista.state.replaceSelection(INDENT));
      return true;
    },
    shift: indentLess,
  },
]);

/** Colora le righe visibili con il tokenizzatore di Portman. */
function colorazione(evidenzia: (riga: string) => Token[]): Extension {
  const decora = (vista: EditorView): DecorationSet => {
    const costruttore = new RangeSetBuilder<Decoration>();

    for (const { from, to } of vista.visibleRanges) {
      for (let posizione = from; posizione <= to; ) {
        const riga = vista.state.doc.lineAt(posizione);
        let inizio = riga.from;

        for (const token of evidenzia(riga.text)) {
          const fine = inizio + token.text.length;
          if (fine > inizio) costruttore.add(inizio, fine, Decoration.mark({ class: COLORI[token.type] }));
          inizio = fine;
        }

        posizione = riga.to + 1;
      }
    }

    return costruttore.finish();
  };

  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(vista: EditorView) {
        this.decorations = decora(vista);
      }

      update(aggiornamento: ViewUpdate) {
        if (aggiornamento.docChanged || aggiornamento.viewportChanged) {
          this.decorations = decora(aggiornamento.view);
        }
      }
    },
    { decorations: (plugin) => plugin.decorations },
  );
}

/** Lo stesso aspetto della console: sfondo scuro in entrambi i temi. */
const tema = EditorView.theme(
  {
    "&": { height: "100%", fontSize: "13px", backgroundColor: "transparent" },
    "&.cm-focused": { outline: "none" },
    ".cm-scroller": { fontFamily: "var(--font-mono)", lineHeight: String(INTERLINEA) },
    ".cm-content": { padding: "12px 0", caretColor: "white" },
    ".cm-line": { padding: "0 12px" },
    ".cm-cursor": { borderLeftColor: "white" },
    "&.cm-focused .cm-selectionBackground, .cm-selectionBackground": {
      backgroundColor: "rgb(255 255 255 / 0.15)",
    },
    ".cm-gutters": {
      backgroundColor: "rgb(0 0 0 / 0.2)",
      borderRight: "1px solid rgb(255 255 255 / 0.1)",
      color: "#525252",
    },
    ".cm-foldGutter .cm-gutterElement": { cursor: "pointer", padding: "0 4px" },
    ".cm-foldPlaceholder": {
      backgroundColor: "rgb(255 255 255 / 0.08)",
      border: "none",
      color: "#a3a3a3",
      padding: "0 6px",
    },
    ".cm-placeholder": { color: "#525252" },
    ".cm-panels": { backgroundColor: "rgb(0 0 0 / 0.3)", color: "inherit" },
    ".cm-panels-top": { borderBottom: "1px solid rgb(255 255 255 / 0.1)" },
    ".cm-search": { fontSize: "13px" },
    ".cm-search input, .cm-search button": { fontSize: "12px" },
    ".cm-textfield": {
      backgroundColor: "rgb(0 0 0 / 0.3)",
      border: "1px solid rgb(255 255 255 / 0.15)",
      borderRadius: "4px",
      color: "inherit",
    },
    ".cm-button": {
      backgroundImage: "none",
      backgroundColor: "rgb(255 255 255 / 0.08)",
      border: "none",
      borderRadius: "4px",
      color: "inherit",
    },
    ".cm-searchMatch": { backgroundColor: "rgb(251 191 36 / 0.25)" },
    ".cm-searchMatch.cm-searchMatch-selected": { backgroundColor: "rgb(251 191 36 / 0.55)" },
  },
  { dark: true },
);

/** Testi del pannello di ricerca: CodeMirror li chiama con le sue frasi inglesi. */
const frasi = (t: Traduttore) =>
  EditorState.phrases.of({
    Find: t.t("editor.cerca"),
    Replace: t.t("editor.sostituisciCon"),
    next: t.t("editor.successiva"),
    previous: t.t("editor.precedente"),
    all: t.t("editor.tutte"),
    "match case": t.t("editor.maiuscole"),
    regexp: t.t("editor.regex"),
    "by word": t.t("editor.parolaIntera"),
    replace: t.t("editor.sostituisci"),
    "replace all": t.t("editor.sostituisciTutte"),
    close: t.t("editor.chiudi"),
    "Folded lines": t.t("editor.righeRipiegate"), // i18n-ignora: chiave di CodeMirror
    "Unfolded lines": t.t("editor.righeAperte"), // i18n-ignora: chiave di CodeMirror
    "Fold line": t.t("editor.ripiega"), // i18n-ignora: chiave di CodeMirror
    "Unfold line": t.t("editor.apri"), // i18n-ignora: chiave di CodeMirror
  });

export function CodeEditor({
  value,
  onChange,
  linguaggio,
  readOnly = false,
  righe = 22,
  altezza,
  placeholder,
  numeriDiRiga = true,
  espandibile = false,
  ricercabile = false,
  ricercaIniziale,
  titolo,
  azioni,
}: CodeEditorProps) {
  const vistaRef = useRef<EditorView | null>(null);
  const [espanso, setEspanso] = useState(false);
  const [copiato, setCopiato] = useState(false);
  const t = useT();
  // Letto da `monta`, che non deve rifare l'editor a ogni cambio di lingua.
  const traduttore = useRef(t);
  traduttore.current = t;

  // Letti dall'editor già creato: ricrearlo a ogni battuta perderebbe cursore e storia.
  const valoreRef = useRef(value);
  valoreRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  /**
   * Crea l'editor nel contenitore montato.
   *
   * Un riferimento a funzione, non un effetto: a schermo intero il riquadro
   * finisce in un portale, cioè in un contenitore nuovo, e l'editor va rifatto
   * lì partendo dal testo corrente.
   */
  const monta = useCallback(
    (contenitore: HTMLDivElement | null) => {
      if (!contenitore) return;

      const vista = new EditorView({
        parent: contenitore,
        state: EditorState.create({
          doc: valoreRef.current,
          extensions: [
            tema,
            frasi(traduttore.current),
            history(),
            drawSelection(),
            indentUnit.of(INDENT),
            rientroYaml,
            colorazione(EVIDENZIATORI[linguaggio]),
            numeriDiRiga ? [lineNumbers(), codeFolding(), foldGutter(), ripiegaPerRientro] : [],
            ricercabile ? search({ top: true }) : [],
            placeholder ? segnaposto(placeholder) : [],
            EditorState.readOnly.of(readOnly),
            tasti,
            keymap.of([
              ...defaultKeymap,
              ...historyKeymap,
              ...foldKeymap,
              ...(ricercabile ? searchKeymap : []),
            ]),
            EditorView.updateListener.of((aggiornamento) => {
              const daFuori = aggiornamento.transactions.some((t) => t.annotation(Transaction.remote));
              if (aggiornamento.docChanged && !daFuori) {
                onChangeRef.current?.(aggiornamento.state.doc.toString());
              }
            }),
          ],
        }),
      });
      vistaRef.current = vista;

      return () => {
        vista.destroy();
        if (vistaRef.current === vista) vistaRef.current = null;
      };
    },
    [linguaggio, readOnly, numeriDiRiga, ricercabile, placeholder],
  );

  /**
   * Un valore cambiato da fuori entra nell'editor sostituendo solo la parte
   * diversa: rimpiazzare tutto sposterebbe il cursore all'inizio.
   */
  useEffect(() => {
    const vista = vistaRef.current;
    if (!vista) return;

    const attuale = vista.state.doc.toString();
    if (attuale === value) return;

    let inizio = 0;
    while (inizio < attuale.length && attuale[inizio] === value[inizio]) inizio++;
    let fine = 0;
    while (
      fine < attuale.length - inizio &&
      fine < value.length - inizio &&
      attuale[attuale.length - 1 - fine] === value[value.length - 1 - fine]
    ) {
      fine++;
    }

    vista.dispatch({
      changes: { from: inizio, to: attuale.length - fine, insert: value.slice(inizio, value.length - fine) },
      annotations: Transaction.remote.of(true),
    });
  }, [value]);

  useEffect(() => {
    const vista = vistaRef.current;
    if (!ricercaIniziale || !vista) return;

    openSearchPanel(vista);
    vista.dispatch({ effects: setSearchQuery.of(new SearchQuery({ search: ricercaIniziale })) });
    findNext(vista);
  }, [ricercaIniziale]);

  /**
   * Porta il testo negli appunti.
   *
   * Dove gli appunti non sono accessibili — un'origine non sicura, un permesso
   * negato — il ripiego è selezionare tutto: non copia al posto dell'utente, ma
   * gli lascia il gesto che conosce invece di un bottone che non fa nulla.
   */
  const copia = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiato(true);
      setTimeout(() => setCopiato(false), 1500);
    } catch {
      const vista = vistaRef.current;
      vista?.focus();
      vista?.dispatch({ selection: { anchor: 0, head: vista.state.doc.length } });
    }
  };

  const altezzaCss = espanso
    ? "100%"
    : altezza !== undefined
      ? `${altezza}px`
      : `${righe * INTERLINEA + 1.5}rem`;

  /**
   * A schermo intero: Escape chiude e la pagina sotto non scorre.
   *
   * Il ripristino dello scorrimento sta nella funzione di pulizia, così avviene
   * anche se il componente viene smontato mentre è espanso. Con la ricerca
   * aperta, Escape chiude prima quella: CodeMirror lo consuma e qui non arriva.
   */
  useEffect(() => {
    if (!espanso) return;

    const precedente = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const chiudiConEsc = (evento: KeyboardEvent) => {
      if (evento.key === "Escape" && !evento.defaultPrevented) setEspanso(false);
    };
    window.addEventListener("keydown", chiudiConEsc);

    return () => {
      document.body.style.overflow = precedente;
      window.removeEventListener("keydown", chiudiConEsc);
    };
  }, [espanso]);

  const riquadro = (
    <div
      ref={monta}
      className={cx(
        "console overflow-hidden border border-white/10 text-neutral-100",
        espanso ? "min-h-0 flex-1 rounded-xl" : "rounded-lg",
      )}
      style={{ height: altezzaCss }}
    />
  );

  if (!espanso) {
    return (
      // `group`: i comandi compaiono passando sul riquadro, non solo
      // esattamente su di loro — invisibili com'erano, andavano indovinati.
      <div className="group relative">
        {riquadro}

        <div className="absolute bottom-2 right-4 z-10 flex gap-1 opacity-0 transition focus-within:opacity-100 group-hover:opacity-100 hover:opacity-100">
          <button
            onClick={copia}
            title={t.t(copiato ? "comune.copiato" : "editor.copiaTutto")}
            aria-label={t.t("editor.copiaTutto")}
            className={cx(
              "rounded-md bg-black/40 p-1.5 hover:text-white",
              copiato ? "text-emerald-400" : "text-neutral-400",
            )}
          >
            {copiato ? <IconaFatto /> : <IconaCopia />}
          </button>

          {ricercabile && (
            <button
              onClick={() => vistaRef.current && openSearchPanel(vistaRef.current)}
              title={t.t("editor.cercaScorciatoia")}
              aria-label={t.t("editor.cerca")}
              className="rounded-md bg-black/40 p-1.5 text-neutral-400 hover:text-white"
            >
              <IconaCerca />
            </button>
          )}

          {espandibile && (
            <button
              onClick={() => setEspanso(true)}
              title={t.t("editor.schermoIntero")}
              aria-label={t.t("editor.schermoIntero")}
              className="rounded-md bg-black/40 p-1.5 text-neutral-400 hover:text-white"
            >
              <IconaEspandi />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Portato su `document.body`: dentro la pagina, un antenato con `overflow`
  // nascosto o una trasformazione limiterebbe un elemento a posizione fissa.
  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col gap-3 bg-[var(--superficie)] p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium">{titolo ?? t.t("editor.titolo")}</span>
        <span className="text-xs tenue">{t.t("editor.aiutoSchermoIntero", { righe: value.split("\n").length })}</span>

        <div className="ml-auto flex items-center gap-2">
          <Button onClick={copia}>{t.t(copiato ? "comune.copiato" : "comune.copia")}</Button>
          {azioni}
          <Button onClick={() => setEspanso(false)}>{t.t("comune.chiudi")}</Button>
        </div>
      </div>

      {riquadro}
    </div>,
    document.body,
  );
}

function IconaCerca() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-4.5-4.5" strokeLinecap="round" />
    </svg>
  );
}

function IconaCopia() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" strokeLinecap="round" />
    </svg>
  );
}

function IconaFatto() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="m5 13 4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconaEspandi() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M4 9V5a1 1 0 0 1 1-1h4M20 15v4a1 1 0 0 1-1 1h-4M15 4h4a1 1 0 0 1 1 1v4M9 20H5a1 1 0 0 1-1-1v-4" strokeLinecap="round" />
    </svg>
  );
}
